import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MailCheck, Send } from 'lucide-react';
import { ApiError, api } from '@/lib/api';
import { Alert, Button, Card, Field, Input } from '@/components/ui';
import { AuthShell } from '@/layouts/AuthShell';

/**
 * Request a password reset link.
 *
 * The confirmation never says whether the address was found. Telling an
 * anonymous caller "no account with that email" turns this form into a way to
 * discover who is a member.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  if (sent) {
    return (
      <AuthShell title="Check your email" description="If we know that address, a link is on its way.">
        <Card className="p-6 sm:p-8">
          <span className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-success-soft text-success">
            <MailCheck size={22} />
          </span>
          <p className="mb-6 leading-relaxed text-foreground-secondary">
            We have sent a password reset link to <strong className="text-foreground">{email}</strong>{' '}
            if an account exists for it. The link expires in one hour.
          </p>
          <Link to="/login">
            <Button full>Back to sign in</Button>
          </Link>
        </Card>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Reset your password"
      description="Give us the email address on your account and we will send you a link."
      footer={
        <Link
          to="/login"
          className="inline-flex w-full items-center justify-center gap-1.5 text-sm font-semibold text-foreground-secondary transition-colors hover:text-primary"
        >
          <ArrowLeft size={14} /> Back to sign in
        </Link>
      }
    >
      <Card className="p-6 sm:p-8">
        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          {error && <Alert tone="danger">{error}</Alert>}

          <Field label="Email address" htmlFor="email" required>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </Field>

          <Button type="submit" full size="lg" loading={loading}>
            <Send size={16} /> Send reset link
          </Button>
        </form>
      </Card>
    </AuthShell>
  );
}
